import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';
import { useSpring, animated } from 'react-spring';
import { Transition } from 'react-spring/renderprops.cjs';

import { pills as Pills, Icon } from '@breezeway/brzw-components';
import {
  TASK_TOOLTIP_CLASS_NAME as CLASSNAME,
  TASK_TOOLTIP_HORIZONTAL_POSITIONS,
  TASK_TOOLTIP_VERTICAL_POSITIONS,
} from './constants';
import StatusPill from '../status_pill';

import styles from './task_tooltip.scss';
import stylesTooltip from '../task_dropdown_list/task_dropdown_list.scss';

const { PILL_SIZES } = Pills.constants;
const TASK_FULL_WIDTH = 24;
const taskButtonWrapperClass = 'brzw-calendar-single-task__wrapper';

const sharedVariables = {
  TASK_TOOLTIP_HEIGHT: _.parseInt(styles.TASK_TOOLTIP_HEIGHT),
  TASK_TOOLTIP_WIDTH: _.parseInt(styles.TASK_TOOLTIP_WIDTH),
  TASK_TOOLTIP_WIDTH_MAX: _.parseInt(styles.TASK_TOOLTIP_WIDTH_MAX),
  TASK_DROPDOWN_CONTAINER_SIZE: _.parseInt(stylesTooltip.TASK_DROPDOWN_CONTAINER_SIZE),
  TASK_DROPDOWN_LIST_OPTION_HEIGHT: _.parseInt(stylesTooltip.TASK_DROPDOWN_LIST_OPTION_HEIGHT),
  TASK_DROPDOWN_LIST_PADDING: _.parseInt(stylesTooltip.TASK_DROPDOWN_LIST_PADDING),
};

const verticalPosition = (
  displayTooltipInPortal,
  taskComponent,
  taskRef,
  paddingVerticalRemoval,
  containerHeight,
) => {
  const position = _.invoke(taskRef, 'current.getBoundingClientRect');
  const positionTop = _.get(position, 'top');
  const positionHeight = _.get(position, 'height', 0);
  const positionBottom = _.get(position, 'bottom');

  const statusPillHeight = _.isEmpty(_.get(taskComponent, 'task_status')) ? 20 : 0;
  const tooltipHeight = sharedVariables.TASK_TOOLTIP_HEIGHT - statusPillHeight;
  let isEnoughHeightSpace;

  if (displayTooltipInPortal) {
    isEnoughHeightSpace =
      containerHeight - (positionBottom + paddingVerticalRemoval) >= tooltipHeight;
  } else {
    isEnoughHeightSpace =
      containerHeight - (positionTop + positionHeight + paddingVerticalRemoval) >= tooltipHeight;
  }
  return isEnoughHeightSpace
    ? TASK_TOOLTIP_VERTICAL_POSITIONS.BOTTOM
    : TASK_TOOLTIP_VERTICAL_POSITIONS.TOP;
};

const TaskTooltipContent = ({
  displayTooltipInPortal,
  containerHeight,
  containerWidth,
  isOpen,
  paddingHorizontalRemoval,
  paddingVerticalRemoval,
  taskComponent,
  taskRef,
}) => {
  let bottom;
  let left;
  let top;

  const currentRef = _.get(taskRef, 'current');
  const commentMessage = _.toNumber(taskComponent.comments);
  const issueMessage = _.toNumber(taskComponent.issues);
  const statusCode = _.get(taskComponent, 'task_status.code');
  const statusName = _.get(taskComponent, 'task_status.name');
  const position = _.invoke(currentRef, 'getBoundingClientRect');
  const topPosition = _.get(position, 'top', 0);
  const leftPosition = _.get(position, 'left');

  const isEnoughWidthSpace =
    containerWidth -
      (_.get(position, 'right') - _.get(position, 'width', 0) + paddingHorizontalRemoval) <=
    sharedVariables.TASK_TOOLTIP_WIDTH_MAX;

  const positionHorizontal = isEnoughWidthSpace
    ? TASK_TOOLTIP_HORIZONTAL_POSITIONS.RIGHT
    : TASK_TOOLTIP_HORIZONTAL_POSITIONS.LEFT;

  const positionVertical = useMemo(
    () =>
      verticalPosition(
        displayTooltipInPortal,
        taskComponent,
        taskRef,
        paddingVerticalRemoval,
        containerHeight,
      ),
    [displayTooltipInPortal, taskComponent, taskRef, paddingVerticalRemoval, containerHeight],
  );

  if (displayTooltipInPortal && isOpen) {
    left = leftPosition - sharedVariables.TASK_DROPDOWN_LIST_PADDING - 2 * TASK_FULL_WIDTH;

    top =
      positionVertical === TASK_TOOLTIP_VERTICAL_POSITIONS.BOTTOM
        ? topPosition + sharedVariables.TASK_DROPDOWN_LIST_OPTION_HEIGHT
        : 'initial';
    bottom =
      positionVertical === TASK_TOOLTIP_VERTICAL_POSITIONS.TOP
        ? window.innerHeight - topPosition + paddingVerticalRemoval
        : undefined;
  }

  const animateTooltip = useSpring({ opacity: isOpen ? 1 : 0, delay: 0, config: { duration: 10 } });
  const buttonChild =
    currentRef && _.first(_.invoke(currentRef, 'getElementsByClassName', taskButtonWrapperClass));

  const backgroundColor =
    displayTooltipInPortal && window && buttonChild
      ? _.invoke(
          _.invoke(window, 'getComputedStyle', buttonChild),
          'getPropertyValue',
          'background-color',
        )
      : {};

  return (
    <Transition
      config={{ duration: 0 }}
      duration={0}
      enter={{ opacity: 1 }}
      from={{ opacity: 0 }}
      items={isOpen}
      leave={{ opacity: 0 }}
      native
    >
      {show =>
        show &&
        (props => {
          let stylesTooltipContent = { ...props, ...animateTooltip };
          const displayTooltipCoordinates = displayTooltipInPortal && _.isNumber(left);

          if (displayTooltipCoordinates) {
            stylesTooltipContent = {
              ...stylesTooltipContent,
              bottom,
              left,
              top,
            };
          }

          return (
            <animated.div
              className={classnames(`${CLASSNAME}__tooltip`, {
                '-position-with-portal-tooltip': displayTooltipCoordinates,
                [`-position-${positionHorizontal}`]: positionHorizontal,
                [`-position-${positionVertical}`]: positionVertical,
              })}
              style={stylesTooltipContent}
            >
              <div
                className={classnames(`${CLASSNAME}__border`, {
                  [`${CLASSNAME}__border--${taskComponent.color}`]: taskComponent.color,
                })}
                style={displayTooltipInPortal ? { backgroundColor } : {}}
              />

              <div className={`${CLASSNAME}__container`}>
                <div className={`${CLASSNAME}__title`}>{taskComponent.name}</div>

                <StatusPill
                  className={`${CLASSNAME}__status-pill`}
                  code={statusCode}
                  name={statusName}
                  size={PILL_SIZES.SMALL}
                />

                <div className={`${CLASSNAME}__legend`}>
                  <div className={`${CLASSNAME}__comments`}>
                    <Icon iconName="comments" className={`${CLASSNAME}__icon`} />
                    {commentMessage}
                  </div>

                  <div className={`${CLASSNAME}__issues`}>
                    <Icon iconName="damaged" className={`${CLASSNAME}__icon`} />
                    {issueMessage}
                  </div>
                </div>
              </div>
            </animated.div>
          );
        })
      }
    </Transition>
  );
};

TaskTooltipContent.propTypes = {
  containerHeight: PropTypes.number,
  containerWidth: PropTypes.number,
  displayTooltipInPortal: PropTypes.bool,
  isOpen: PropTypes.bool,
  paddingHorizontalRemoval: PropTypes.number,
  paddingVerticalRemoval: PropTypes.number,
  taskComponent: PropTypes.shape({}),
  taskRef: PropTypes.shape({}),
};

TaskTooltipContent.defaultProps = {
  isOpen: false,
  paddingHorizontalRemoval: 0,
  paddingVerticalRemoval: 0,
  taskComponent: {},
  taskRef: {},
};

export default TaskTooltipContent;
